/**
 * 「下载历史对话」对话框（Alice 的 #modal-vendor-export）：按日期与条数导出用户与助手文字对。
 *
 * 背景：Alice 从 COS 归档里取对话；本仓没有归档，对话来自 request_bodies 里仍在保留期的正文，
 *   后端只导出账号策略打开 allow_vendor_export 的成功聊天，输出去标识（无账号、请求号）。
 * 设计意图：日期、条数在对话框里选，用户与模型沿用页面当前的筛选，只读展示，避免弹窗里再放一套筛选。
 * 关键约束：单次最多 31 天、条数 1 到 2,000,000，前端先校验再请求；这是导出，要管理员角色，调用方负责禁用按钮。
 */
import { useEffect, useState } from 'react'
import { runtimeApi, saveBlob } from '@ap/api-client/admin-runtime'
import { AG_runtime as G, errorTextRuntime } from '@ap/i18n/admin-runtime'
import { useI18n } from '@ap/i18n'
import { useToast } from '@ap/ui'
import { AliceModal, addDays, todayLocal } from '../../../alice/kit-runtime.tsx'
import { fieldStyle } from '../../../alice/kit-runtime2.tsx'

const DAY_MS = 86_400_000

export function VendorDialog({ open, user, model, onClose }: { open: boolean; user: string; model: string; onClose: () => void }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [limit, setLimit] = useState('100000')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    const today = todayLocal()
    setStart(addDays(today, -7))
    setEnd(today)
    setError('')
  }, [open])

  const submit = async () => {
    if (!start || !end) return setError(t(G.vendor.needRange))
    if (start > end) return setError(t(G.vendor.badRange))
    if ((Date.parse(end) - Date.parse(start)) / DAY_MS > 30) return setError(t(G.vendor.tooLong))
    const n = Number(limit)
    if (!Number.isInteger(n) || n < 1 || n > 2_000_000) return setError(t(G.vendor.badLimit))
    setError('')
    setBusy(true)
    try {
      const r = await runtimeApi.exportVendor({ start_date: start, end_date: end, limit: n, user_id: user || undefined, model: model || undefined })
      saveBlob(r.blob, r.info.filename)
      toast.ok(t(G.vendor.done))
      onClose()
    } catch (e) {
      console.error('[logs] 历史对话导出失败', e)
      setError(errorTextRuntime(e, locale))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AliceModal open={open} onClose={onClose} title={t(G.vendor.title)} width={460}>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 14px' }}>{t(G.vendor.copy)}</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t(G.exportDialog.startDate)}
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={{ ...fieldStyle, width: '100%', marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t(G.exportDialog.endDate)}
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={{ ...fieldStyle, width: '100%', marginTop: 4 }} />
        </label>
      </div>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 10 }}>
        {t(G.vendor.maxPairs)}
        <input type="number" min={1} max={2000000} value={limit} onChange={(e) => setLimit(e.target.value)} style={{ ...fieldStyle, width: '100%', marginTop: 4 }} />
      </label>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>
        {t(G.vendor.inherit, {
          user: user ? t(G.vendor.userOf, { id: user }) : t(G.vendor.allUsers),
          model: model ? t(G.vendor.modelContains, { m: model }) : '',
        })}
      </div>
      {error && <div style={{ fontSize: 12, color: 'var(--error-text)', marginBottom: 6 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
          {t(G.common.cancel)}
        </button>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void submit()}>
          {busy ? t(G.exportDialog.working) : t(G.vendor.submit)}
        </button>
      </div>
    </AliceModal>
  )
}
